/* MorningBrief — Sofia's daily summary card in cream/rose journal style.
 * Shows date, weather, namnsdag, standard tasks, weekday focus room with time-based micro-plan. */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Sun, Cloud, Plus, Trash2, Edit2, Check, Clock, Sparkles, ChevronRight, X, PhoneCall,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_OPTIONS = [5, 10, 15, 20, 30, 45];

export default function MorningBrief() {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weather, setWeather] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newTaskText, setNewTaskText] = useState('');
  const [newTaskEmoji, setNewTaskEmoji] = useState('✨');
  const [editing, setEditing] = useState(null);
  const [minutes, setMinutes] = useState(15);
  const [plan, setPlan] = useState(null);
  const [planning, setPlanning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/daily-brief/today`, { withCredentials: true });
      setBrief(data);
    } catch { toast.error('Kunde inte ladda dagens brief'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Try to get weather using geolocation (best-effort, silent fail)
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { data } = await axios.get(
            `${API}/weather/current?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}`,
            { withCredentials: true }
          );
          setWeather(data);
        } catch { /* silent */ }
      },
      () => { /* denied — silent */ },
      { timeout: 6000, maximumAge: 30 * 60 * 1000 }
    );
  }, []);

  const toggle = async (taskId) => {
    try {
      await axios.post(`${API}/daily-brief/standard-tasks/${taskId}/toggle`, {}, { withCredentials: true });
      await load();
    } catch { toast.error('Kunde inte spara'); }
  };

  const addTask = async () => {
    if (newTaskText.trim().length < 2) { toast.error('Skriv minst 2 tecken'); return; }
    try {
      await axios.post(`${API}/daily-brief/standard-tasks`, { text: newTaskText, emoji: newTaskEmoji }, { withCredentials: true });
      setNewTaskText(''); setNewTaskEmoji('✨'); setAdding(false);
      toast.success('Tillagt 💜');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kunde inte spara'); }
  };

  const delTask = async (id) => {
    if (!window.confirm('Ta bort uppgiften?')) return;
    try {
      await axios.delete(`${API}/daily-brief/standard-tasks/${id}`, { withCredentials: true });
      await load();
    } catch { toast.error('Kunde inte ta bort'); }
  };

  const saveEdit = async (id, text, emoji) => {
    try {
      await axios.patch(`${API}/daily-brief/standard-tasks/${id}`, { text, emoji }, { withCredentials: true });
      setEditing(null);
      await load();
    } catch { toast.error('Kunde inte spara'); }
  };

  const buildPlan = async () => {
    setPlanning(true);
    try {
      const { data } = await axios.post(`${API}/daily-brief/focus-room/plan`, { minutes }, { withCredentials: true });
      setPlan(data);
    } catch { toast.error('Kunde inte bygga plan'); }
    finally { setPlanning(false); }
  };

  if (loading || !brief) {
    return (
      <div className="text-center py-12 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Laddar brief…
      </div>
    );
  }

  const doneCount = brief.standard_tasks.filter((t) => t.done).length;

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="morning-brief">
      {/* Hero card — cream journal-style */}
      <div
        className="relative rounded-3xl p-6 border overflow-hidden shadow-sm"
        style={{
          background: 'linear-gradient(135deg, #FFF9F1 0%, #FFEEF2 55%, #FFF5FA 100%)',
          borderColor: '#F4C2C2',
        }}
        data-testid="morning-brief-hero"
      >
        {/* Doodled corner sparkles */}
        <div className="absolute right-4 top-4 opacity-25 text-3xl leading-none pointer-events-none select-none">☁️</div>
        <div className="absolute left-6 top-16 opacity-15 text-2xl leading-none pointer-events-none select-none">✿</div>
        <div className="absolute right-8 bottom-4 opacity-15 text-2xl leading-none pointer-events-none select-none">🌸</div>

        <div className="text-[11px] uppercase font-semibold tracking-widest text-rose-800/70">
          {brief.greeting}
        </div>
        <h1
          className="text-3xl font-bold text-rose-950 mt-1"
          style={{ fontFamily: '"Caveat", "Marker Felt", ui-serif, Georgia, serif' }}
        >
          Idag är det {brief.date_pretty} 💗
        </h1>

        {/* Info chips */}
        <div className="flex flex-wrap gap-2 mt-3" data-testid="morning-info-chips">
          {weather && weather.temp !== undefined && (
            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/80 border border-amber-200 px-3 py-1 text-sm text-amber-900 shadow-sm" data-testid="morning-weather">
              {String(weather.condition || '').toLowerCase().includes('sol') || String(weather.condition || '').toLowerCase().includes('clear')
                ? <Sun className="w-3.5 h-3.5" />
                : <Cloud className="w-3.5 h-3.5" />}
              <span><b>{Math.round(weather.temp)}°</b> {weather.condition || weather.description || ''}</span>
            </div>
          )}
          <div className="inline-flex items-center gap-1.5 rounded-full bg-white/80 border border-rose-200 px-3 py-1 text-sm text-rose-900 shadow-sm" data-testid="morning-namesday">
            <span>🎉</span>
            <span>
              {brief.namesday.names.length > 0
                ? <><b>{brief.namesday.label}</b> har namnsdag</>
                : brief.namesday.label}
            </span>
          </div>
        </div>
      </div>

      {/* Standard daily tasks */}
      <div className="rounded-2xl bg-white border border-rose-100 p-4" data-testid="morning-standard-tasks">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-rose-500" /> Sedvanliga uppgifter
          </div>
          <span className="text-[11px] text-slate-500">{doneCount} / {brief.standard_tasks.length} klara</span>
        </div>
        <div className="space-y-1.5">
          {brief.standard_tasks.map((t) => (
            editing === t.id ? (
              <TaskEditor
                key={t.id}
                initial={t}
                onCancel={() => setEditing(null)}
                onSave={(text, emoji) => saveEdit(t.id, text, emoji)}
              />
            ) : (
              <div
                key={t.id}
                className={`flex items-center gap-2 p-2 rounded-lg group ${t.done ? 'bg-emerald-50' : 'hover:bg-rose-50/40'}`}
                data-testid={`morning-task-${t.id}`}
              >
                <button
                  type="button"
                  onClick={() => toggle(t.id)}
                  data-testid={`morning-task-toggle-${t.id}`}
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    t.done ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-rose-300 bg-white hover:border-rose-500'
                  }`}
                  aria-label={t.done ? 'Ångra' : 'Klart'}
                >
                  {t.done && <Check className="w-3 h-3" strokeWidth={3} />}
                </button>
                <div className="text-base flex-shrink-0">{t.emoji || '✨'}</div>
                <div className={`text-sm flex-1 ${t.done ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
                  {t.text}
                </div>
                <div className="opacity-0 group-hover:opacity-100 flex gap-1">
                  <button onClick={() => setEditing(t.id)} className="text-slate-400 hover:text-rose-600" aria-label="Redigera">
                    <Edit2 className="w-3 h-3" />
                  </button>
                  <button onClick={() => delTask(t.id)} className="text-slate-400 hover:text-red-500" aria-label="Ta bort">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )
          ))}
          {adding ? (
            <div className="rounded-lg bg-rose-50 border border-rose-200 p-2 flex gap-1.5 items-center">
              <input
                type="text"
                value={newTaskEmoji}
                onChange={(e) => setNewTaskEmoji(e.target.value)}
                maxLength={4}
                className="w-12 text-center rounded border border-slate-200 py-1 focus:outline-none focus:ring-2 focus:ring-rose-200"
              />
              <input
                type="text"
                value={newTaskText}
                onChange={(e) => setNewTaskText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addTask()}
                placeholder="Ny sedvanlig uppgift"
                data-testid="morning-task-input"
                className="flex-1 text-sm rounded border border-slate-200 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-rose-200"
                autoFocus
              />
              <button
                onClick={addTask}
                data-testid="morning-task-save"
                className="text-xs px-2 py-1 rounded-full bg-rose-500 text-white hover:bg-rose-600"
              >
                Spara
              </button>
              <button
                onClick={() => { setAdding(false); setNewTaskText(''); }}
                className="text-slate-400 hover:text-slate-700"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setAdding(true)}
              data-testid="morning-task-add-btn"
              className="w-full text-xs px-3 py-1.5 rounded-xl bg-rose-50 hover:bg-rose-100 border border-dashed border-rose-300 text-rose-800 inline-flex items-center justify-center gap-1"
            >
              <Plus className="w-3 h-3" /> Lägg till
            </button>
          )}
        </div>
      </div>

      {/* Weekday focus room */}
      <div
        className="rounded-2xl p-4 border"
        style={{ background: 'linear-gradient(135deg, #FFFAE8 0%, #FFF0F0 100%)', borderColor: '#F5D5B0' }}
        data-testid="morning-focus-room"
      >
        <div className="flex items-start gap-3">
          <div className="text-4xl leading-none">{brief.focus_room.emoji}</div>
          <div className="flex-1">
            <div className="text-[11px] uppercase font-semibold tracking-widest text-amber-800/70">
              {brief.focus_room.weekday_label} — fokusdag
            </div>
            <h2 className="text-xl font-bold text-amber-950" style={{ fontFamily: '"Caveat", ui-serif, Georgia, serif' }}>
              Idag är det fokus på {brief.focus_room.room}
            </h2>
            <p className="text-xs text-amber-900/70 mt-1">Hur mycket tid har du?</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 mt-3">
          {TIME_OPTIONS.map((t) => (
            <button
              key={t}
              onClick={() => setMinutes(t)}
              data-testid={`morning-time-${t}`}
              className={`px-3 py-1 rounded-full text-xs font-semibold border-2 transition ${
                minutes === t
                  ? 'bg-amber-500 border-amber-500 text-white shadow-sm'
                  : 'bg-white border-amber-200 text-amber-800 hover:border-amber-400'
              }`}
            >
              {t} min
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={buildPlan}
          disabled={planning}
          data-testid="morning-build-plan"
          className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-amber-500 to-rose-500 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
        >
          {planning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Clock className="w-3.5 h-3.5" />}
          Bygg {minutes}-min-plan · effektivitet max
        </button>

        {plan && (
          <div className="mt-4 rounded-xl bg-white border border-amber-200 p-3" data-testid="morning-plan-result">
            <div className="text-xs uppercase font-bold tracking-wider text-amber-800 mb-2">
              {plan.emoji} {plan.room} — {plan.total_minutes} min
            </div>
            <ol className="space-y-1.5">
              {plan.steps.map((s) => (
                <li key={s.n} className="flex items-start gap-2 text-sm" data-testid={`morning-plan-step-${s.n}`}>
                  <div className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {s.n}
                  </div>
                  <div className="flex-1">
                    <div className="text-slate-900">{s.task}</div>
                  </div>
                  <div className="text-[11px] text-amber-700 font-semibold flex-shrink-0 mt-1">
                    {s.minutes} min
                  </div>
                </li>
              ))}
            </ol>
            <p className="text-[11px] text-amber-800/60 italic mt-3">
              💡 {plan.farmor_hint}
            </p>
          </div>
        )}
      </div>

      {/* Weekday focus rooms overview */}
      <div className="rounded-2xl bg-white border border-slate-100 p-4">
        <div className="text-xs uppercase font-semibold tracking-wider text-slate-500 mb-2">Veckans fokusrum</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
          {brief.focus_rooms_all.map((r) => (
            <div
              key={r.weekday}
              className={`rounded-lg p-2 text-center ${
                r.weekday === brief.focus_room.weekday
                  ? 'bg-amber-50 border-2 border-amber-300'
                  : 'bg-slate-50 border border-slate-100'
              }`}
              data-testid={`morning-week-${r.weekday}`}
            >
              <div className="text-xl">{r.emoji}</div>
              <div className="text-[10px] uppercase font-semibold text-slate-500 mt-1">{r.weekday_label.slice(0, 3)}</div>
              <div className="text-[11px] text-slate-800 font-medium mt-0.5 truncate">{r.room}</div>
            </div>
          ))}
        </div>
      </div>

      <p className="text-[11px] text-slate-400 italic text-center pt-2">
        <PhoneCall className="w-3 h-3 inline mr-1" />
        Vill du höra en röst? Ring Farmor Corgi under Städ 🐶
      </p>
    </div>
  );
}

function TaskEditor({ initial, onCancel, onSave }) {
  const [text, setText] = useState(initial.text);
  const [emoji, setEmoji] = useState(initial.emoji || '✨');
  return (
    <div className="rounded-lg bg-rose-50 border border-rose-200 p-2 flex gap-1.5 items-center">
      <input
        type="text" value={emoji} onChange={(e) => setEmoji(e.target.value)} maxLength={4}
        className="w-12 text-center rounded border border-slate-200 py-1"
      />
      <input
        type="text" value={text} onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && onSave(text, emoji)}
        className="flex-1 text-sm rounded border border-slate-200 px-2 py-1"
        autoFocus
      />
      <button onClick={() => onSave(text, emoji)} className="text-xs px-2 py-1 rounded-full bg-emerald-500 text-white">Spara</button>
      <button onClick={onCancel} className="text-slate-400"><X className="w-3.5 h-3.5" /></button>
    </div>
  );
}
